import type { TourLabels, TourStep } from '@/components/wingo/ui/tour';
import type { ShippingDirection } from '@/contexts/DirectionContext';
import type { HelpLang } from '../content/helpContent';

/**
 * Pașii turului ghidat al portalului. Țintele sunt atributele `data-tour` puse în layout
 * (app-sidebar, site-header, bottom-nav) și containerele dashboard-ului (`.stat-grid`, `.dash-grid`).
 * Pe ecrane sub 900px sidebar-ul e ascuns: pașii țintesc bara de jos (`.bottom-nav`) sau butonul ☰.
 * O țintă negăsită în 1s afișează cardul pe centru (comportamentul Tour), deci un pas nu blochează turul.
 */

/** Pragul la care portalul trece pe layout-ul de telefon (claude-design.css). */
export const NARROW_QUERY = '(max-width: 900px)';

type Text = Record<HelpLang, string>;

function isOnScreen(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  // sidebar-ul de pe telefon e tras în afara ecranului cu translateX(-100%)
  return rect.right > 0 && rect.left < window.innerWidth;
}

/** Primul element vizibil dintre selectori — același pas are ținte diferite pe calculator și pe telefon. */
function firstVisible(...selectors: string[]): () => Element | null {
  return () => {
    for (const selector of selectors) {
      const found = Array.from(document.querySelectorAll(selector)).find(isOnScreen);
      if (found) return found;
    }
    return null;
  };
}

function navTarget(path: string): () => Element | null {
  return firstVisible(`.sidebar a.nav-item[href="${path}"]`, `.bottom-nav a.bn-item[href="${path}"]`);
}

export interface TourStepsOptions {
  lang: HelpLang;
  direction: ShippingDirection;
  hasBothDirections: boolean;
  isNarrow: boolean;
  /** Duce utilizatorul pe dashboard și așteaptă cardurile (pașii 3–4 le țintesc). */
  ensureDashboard: () => Promise<void>;
}

export function buildTourSteps({ lang, direction, hasBothDirections, isNarrow, ensureDashboard }: TourStepsOptions): TourStep[] {
  const t = (text: Text) => text[lang];
  const isExport = direction === 'export';
  const steps: TourStep[] = [];

  steps.push({
    id: 'welcome',
    title: t({ ro: 'Bun venit în portalul TLS', en: 'Welcome to the TLS portal' }),
    content: t({
      ro: 'În mai puțin de un minut îți arătăm unde găsești containerele, conosamentele, costurile, ofertele și documentele tale. Poți sări turul oricând și îl reiei din pagina „Ajutor”.',
      en: 'In under a minute we show you where to find your containers, bills of lading, costs, offers and documents. Skip it any time and replay it from the “Help” page.',
    }),
    beforeEnter: ensureDashboard,
  });

  steps.push({
    id: 'navigation',
    target: firstVisible('[data-tour="sidebar-nav"]', '.bottom-nav'),
    side: isNarrow ? 'top' : 'right',
    title: t({ ro: 'Meniul principal', en: 'The main menu' }),
    content: isNarrow
      ? t({
          ro: 'Paginile principale sunt în bara de jos. Restul (Costuri, Asistentul AI, 2FA, Setări, Ajutor) sunt în meniul ☰ din stânga sus.',
          en: 'The main pages are in the bottom bar. The rest (Costs, AI assistant, 2FA, Settings, Help) are in the ☰ menu at the top left.',
        })
      : t({
          ro: isExport
            ? 'De aici ajungi în orice pagină: Dashboard, Containere, Conosamente, Hartă live și Oferte. Mai jos sunt contul, 2FA, setările și ajutorul.'
            : 'De aici ajungi în orice pagină: Dashboard, Containere, Conosamente, Hartă live, Oferte, Costuri și Asistentul AI. Mai jos sunt contul, 2FA, setările și ajutorul.',
          en: isExport
            ? 'From here you reach every page: Dashboard, Containers, Bills of lading, Live map and Offers. Below are your account, 2FA, settings and help.'
            : 'From here you reach every page: Dashboard, Containers, Bills of lading, Live map, Offers, Costs and the AI assistant. Below are your account, 2FA, settings and help.',
        }),
  });

  steps.push({
    id: 'stats',
    target: '.stat-grid',
    side: 'bottom',
    beforeEnter: ensureDashboard,
    title: t({ ro: 'Situația pe scurt', en: 'The overview' }),
    content: isExport
      ? t({
          ro: 'Containerele active pe statusuri, ce e în tranzit, ce pleacă în următoarele 14 zile și ultimele containere descărcate.',
          en: 'Active containers by status, what is in transit, what departs in the next 14 days and the latest discharged containers.',
        })
      : t({
          ro: 'Containerele active pe statusuri, ce e în tranzit, ce sosește în următoarele 14 zile și costurile estimate pe luna sosirii.',
          en: 'Active containers by status, what is in transit, what arrives in the next 14 days and the estimated costs by arrival month.',
        }),
  });

  steps.push({
    id: 'dashboard-cards',
    // antetul primului card (titlu + „Export”); grila întreagă e mai înaltă decât ecranul
    target: '.dash-grid .card-header',
    side: 'bottom',
    beforeEnter: ensureDashboard,
    title: t({ ro: 'Liste gata de exportat', en: 'Lists ready to export' }),
    content: t({
      ro: 'Containerele care vin, plecările de nave, bookingurile în tranzit, ultima ofertă și timpii de tranzit. Butonul „Export” descarcă lista în Excel.',
      en: 'Incoming containers, vessel departures, bookings in transit, the latest offer and transit times. The “Export” button downloads the list to Excel.',
    }),
  });

  steps.push({
    id: 'search',
    target: firstVisible('[data-tour="search"]'),
    side: 'bottom',
    title: t({ ro: 'Caută orice', en: 'Search anything' }),
    content: isNarrow
      ? t({
          ro: 'Cu lupa poți căuta direct containere, B/L-uri, oferte sau nave. Rezultatul se deschide direct într-un pop-up.',
          en: 'Use the magnifier to search containers, bills of lading, offers or vessels. The result opens straight in a pop-up.',
        })
      : t({
          ro: 'Poți căuta direct containere, B/L-uri, oferte sau nave, din orice pagină, cu „Ctrl + K” (pe macOS „⌘K”) sau tasta „/”. Rezultatul se deschide direct într-un pop-up.',
          en: 'Search containers, bills of lading, offers or vessels from any page with “Ctrl + K” (“⌘K” on macOS) or the “/” key. The result opens straight in a pop-up.',
        }),
  });

  if (hasBothDirections) {
    steps.push({
      id: 'direction',
      target: firstVisible('[data-tour="direction"]'),
      side: 'bottom',
      title: t({ ro: 'Import sau export', en: 'Import or export' }),
      content: t({
        ro: 'Contul tău are ambele direcții. Comuți aici; statusurile, coloanele și unele pagini diferă între ele.',
        en: 'Your account has both directions. Switch here; statuses, columns and some pages differ between them.',
      }),
    });
  }

  steps.push({
    id: 'company',
    target: firstVisible('[data-tour="company"]'),
    side: 'bottom',
    title: t({ ro: 'Alege firma', en: 'Choose the company' }),
    content: t({
      ro: 'Dacă lucrezi pentru mai multe firme, filtrezi pe una singură sau vezi „Toate firmele”. Alegerea rămâne salvată.',
      en: 'If you work for several companies, filter to one of them or see “All companies”. Your choice stays saved.',
    }),
  });

  steps.push({
    id: 'containers',
    target: navTarget('/app/containers'),
    side: isNarrow ? 'top' : 'right',
    title: t({ ro: 'Containere', en: 'Containers' }),
    content: isExport
      ? t({
          ro: 'Toate containerele, cu filtre pe status, coloane configurabile, notițe și export Excel. Apasă pe un container pentru feeder, nava mamă și transbordări.',
          en: 'All containers, with status filters, configurable columns, notes and Excel export. Click a container for the feeder, mother vessel and transshipments.',
        })
      : t({
          ro: 'Toate containerele, cu filtre pe status, coloane configurabile, notițe și export Excel. În pop-up-ul containerului, tab-ul „Documente” conține avizarea de sosire, cererea de eliberare și LOI-ul.',
          en: 'All containers, with status filters, configurable columns, notes and Excel export. In the container pop-up, the “Documents” tab contains the arrival notice, the release request and the LOI.',
        }),
  });

  steps.push({
    id: 'bills',
    target: navTarget('/app/bills'),
    side: isNarrow ? 'top' : 'right',
    title: t({ ro: 'Conosamente', en: 'Bills of lading' }),
    content: t({
      ro: 'B/L-urile tale: shipper, consignee, marfă, containere și detalii operaționale, cu aceleași filtre și export în Excel.',
      en: 'Your bills of lading: shipper, consignee, cargo, containers and operational details, with the same filters and Excel export.',
    }),
  });

  steps.push({
    id: 'map',
    target: navTarget('/app/map'),
    side: isNarrow ? 'top' : 'right',
    title: t({ ro: 'Hartă live', en: 'Live map' }),
    content: t({
      ro: 'Unde sunt acum navele cu containerele tale și ce ai în porturi.',
      en: 'Where the vessels carrying your containers are right now and what sits in ports.',
    }),
  });

  steps.push({
    id: 'offers',
    target: navTarget('/app/offers'),
    side: isNarrow ? 'top' : 'right',
    title: t({ ro: 'Oferte', en: 'Offers' }),
    content: t({
      ro: 'Tarifele primite de la TLS, valabile și expirate. „Vezi detalii” deschide oferta completă, cu termenii și condițiile.',
      en: 'The rates you received from TLS, valid and expired. “View details” opens the full offer with its terms and conditions.',
    }),
  });

  if (!isExport) {
    steps.push({
      id: 'costs',
      target: firstVisible('.sidebar [data-tour="costs"]', '.mobile-header-toggle'),
      side: isNarrow ? 'bottom' : 'right',
      title: t({ ro: 'Costuri', en: 'Costs' }),
      content: t({
        ro:
          'Costul estimat al transporturilor pe drum (navlu și cheltuieli locale), tarifele noastre standard și un calculator.' +
          (isNarrow ? ' O găsești în meniul ☰.' : ''),
        en:
          'The estimated cost of shipments on the way (freight and local charges), our standard tariffs and a calculator.' +
          (isNarrow ? ' You will find it in the ☰ menu.' : ''),
      }),
    });

    steps.push({
      id: 'assistant',
      target: firstVisible('.sidebar [data-tour="assistant"]', '.mobile-header-toggle'),
      side: isNarrow ? 'bottom' : 'right',
      title: t({ ro: 'Asistent AI', en: 'AI assistant' }),
      content: t({
        ro: 'Întreabă în cuvinte simple, de exemplu „Ce containere sosesc săptămâna aceasta?”.',
        en: 'Ask in plain words, for example “Which containers arrive this week?”.',
      }),
    });
  }

  if (!isNarrow) {
    steps.push({
      id: 'preferences',
      target: firstVisible('[data-tour="preferences"]'),
      side: 'bottom',
      align: 'end',
      title: t({ ro: 'Limbă și temă', en: 'Language and theme' }),
      content: t({
        ro: 'Comuți între română și engleză și între tema luminoasă și cea întunecată.',
        en: 'Switch between Romanian and English and between the light and dark theme.',
      }),
    });
  }

  steps.push({
    id: 'help',
    target: firstVisible('[data-tour="help"]'),
    side: 'bottom',
    align: 'end',
    title: t({ ro: 'Ajutor oricând', en: 'Help any time' }),
    content: t({
      ro: 'Aici găsești ghidul complet al fiecărei pagini, întrebările frecvente și poți relua acest tur.',
      en: 'Here you find the full guide for every page, the FAQ, and you can replay this tour.',
    }),
  });

  return steps;
}

export function tourLabels(lang: HelpLang): Partial<TourLabels> {
  if (lang === 'en') {
    return {
      next: 'Next',
      back: 'Back',
      skip: 'Skip tour',
      done: 'Got it',
      close: 'Close tour',
      progress: (index, count) => `Step ${index} of ${count}`,
      stepAnnounce: (index, count, title) => `Step ${index} of ${count}: ${title}`,
      skipped: 'Tour closed',
      completed: 'Tour completed',
    };
  }
  return {
    next: 'Continuă',
    back: 'Înapoi',
    skip: 'Sari peste tur',
    done: 'Am înțeles',
    close: 'Închide turul',
    progress: (index, count) => `Pasul ${index} din ${count}`,
    stepAnnounce: (index, count, title) => `Pasul ${index} din ${count}: ${title}`,
    skipped: 'Turul a fost închis',
    completed: 'Turul s-a încheiat',
  };
}
